import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Bell, Send, Users, Calendar, Mail, RefreshCw, AlertTriangle } from 'lucide-react';

const NOTIFICATION_TYPES = [
  { value: 'info', label: 'Info', color: 'bg-gray-100 text-gray-700' },
  { value: 'renewal', label: 'Renewal', color: 'bg-blue-100 text-blue-700' },
  { value: 'payment', label: 'Payment', color: 'bg-green-100 text-green-700' },
  { value: 'card_expiry', label: 'Card Expiry', color: 'bg-orange-100 text-orange-700' },
];

export default function AdminNotifications() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    user_ids: [],
    group_id: '',
    send_email: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, clientsRes, groupsRes] = await Promise.all([
        adminAPI.getNotificationStats(),
        adminAPI.getAllClients(),
        adminAPI.getAllGroups(),
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    
    if (formData.user_ids.length === 0 && !formData.group_id) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSending(true);
    try {
      const response = await adminAPI.sendNotification(formData);
      toast.success(response.data.message);
      setShowSendDialog(false);
      setFormData({
        title: '',
        message: '',
        type: 'info',
        user_ids: [],
        group_id: '',
        send_email: false,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleSendRenewalReminders = async (days) => {
    setSendingReminders(true);
    try {
      const response = await adminAPI.sendRenewalReminders(days);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  const selectAllClients = () => {
    setFormData(prev => ({
      ...prev,
      user_ids: clients.map(c => c.id)
    }));
  };

  const clearSelection = () => {
    setFormData(prev => ({
      ...prev,
      user_ids: [],
      group_id: ''
    }));
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Notifications Management</h1>
            <p className="text-muted-foreground">Send notifications and reminders to clients</p>
          </div>
          <Button onClick={() => setShowSendDialog(true)} data-testid="send-notification-btn">
            <Send className="w-4 h-4 mr-2" />
            Send Notification
          </Button>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Bell className="w-8 h-8 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Total Sent</p>
                      <p className="text-2xl font-bold">{stats?.total || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-8 h-8 text-yellow-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Unread</p>
                      <p className="text-2xl font-bold">{stats?.unread || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Last 24h</p>
                      <p className="text-2xl font-bold">{stats?.recent_24h || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Clients</p>
                      <p className="text-2xl font-bold">{clients.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Notification Types Breakdown */}
            {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Notifications by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(stats.by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center gap-2">
                        <Badge variant="outline" className={NOTIFICATION_TYPES.find(t => t.value === type)?.color}>
                          {type}
                        </Badge>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Send automated notifications to clients</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="w-5 h-5 text-blue-500" />
                      <h4 className="font-semibold">Renewal Reminders</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Send reminders to clients with services expiring soon
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSendRenewalReminders(7)}
                        disabled={sendingReminders}
                      >
                        7 days
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSendRenewalReminders(14)}
                        disabled={sendingReminders}
                      >
                        14 days
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleSendRenewalReminders(30)}
                        disabled={sendingReminders}
                      >
                        30 days
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      <h4 className="font-semibold">Card Expiry Alerts</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Automatically handled via Stripe webhooks when cards are about to expire
                    </p>
                    <Badge variant="secondary">Automatic</Badge>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Send className="w-5 h-5 text-green-500" />
                      <h4 className="font-semibold">Custom Notification</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Send a custom notification to selected clients or groups
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowSendDialog(true)}
                    >
                      Compose
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Send Notification Dialog */}
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send Notification</DialogTitle>
              <DialogDescription>
                Send a notification to selected clients or groups
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Notification title"
                  data-testid="notification-title-input"
                />
              </div>

              <div>
                <Label>Message *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Write your message..."
                  rows={4}
                  data-testid="notification-message-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    {NOTIFICATION_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Group (optional)</Label>
                  <select
                    value={formData.group_id}
                    onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.user_ids?.length || 0} members)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.send_email}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
                />
                <Label>Also send via email</Label>
              </div>

              {/* Client Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Select Recipients ({formData.user_ids.length} selected)</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllClients}>Select All</Button>
                    <Button variant="ghost" size="sm" onClick={clearSelection}>Clear</Button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                  {clients.map(client => (
                    <label
                      key={client.id}
                      className="flex items-center gap-2 p-2 hover:bg-accent rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.user_ids.includes(client.id)}
                        onChange={() => toggleUserSelection(client.id)}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{client.full_name}</span>
                        <span className="text-sm text-muted-foreground ml-2">{client.email}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSendDialog(false)} disabled={sending}>
                Cancel
              </Button>
              <Button onClick={handleSendNotification} disabled={sending} data-testid="send-btn">
                {sending ? 'Sending...' : 'Send Notification'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
